import React, { Component } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Header, Grid, Divider, Flag, Popup, Icon } from 'semantic-ui-react';
import cn from 'classnames';
import { Accounts, Portfolio, Storage } from 'actions';
import { colors, nbColors } from 'utils/colors';
import {
  notWealthica,
  hasNegativeWeight,
  getHoldTotalMarketValue,
} from 'utils/helpers';
import { Segment, Button } from 'components';
import { HoldingTable, GicTable, AssetChart } from '../components';
import { translation } from '../../../Translation/accounts';
import { hasCurrencyOptions } from '../../../../utils/helpers';

class MultipleAccount extends Component {
  constructor(props) {
    super(props);
    this.state = { tablePage: 1 };
  }

  componentDidMount() {
    const { selectedAccountList, history } = this.props;

    if (selectedAccountList.length === 0) {
      history.push('/accounts/user');
    }
  }

  getFlag = (currency) => (currency === 'USD' ? 'us' : 'ca');

  analyze = (holdings, region) => {
    const { dispatch, history, selectedAccountList } = this.props;
    const gicHoldings = holdings.filter((holding) => holding.gic_ticker);
    Storage.removeItem('pwpa-has-exclude-holding');

    const concatAccountNames = selectedAccountList
      .map((acc) => acc.user_institution)
      .join(' - ');
    return dispatch(
      Accounts.analyze(
        holdings,
        history,
        { user_institution: concatAccountNames },
        gicHoldings,
        region
      )
    );
  };

  parseNum = (num) => {
    num = !num ? 0 : Math.round(num * 100) / 100;
    return num.toLocaleString(undefined, {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  increaseTablePage = () => {
    const { tablePage } = this.state;
    this.setState({ tablePage: tablePage + 1 });
  };

  decreaseTablePage = () => {
    const { tablePage } = this.state;
    this.setState({ tablePage: tablePage - 1 });
  };

  render() {
    const {
      dispatch,
      history,
      selectedAccountOverview,
      selectedAccountList,
      pwpaExchangeRate,
      user_region,
      partnerName,
      french,
      language,
    } = this.props;
    const { tablePage } = this.state;
    const validHoldings =
      selectedAccountOverview !== null
        ? selectedAccountOverview.holdings.filter(
            (holding) => !!holding.security_detail
          )
        : null;
    const usdHolding = validHoldings.find((holding) => {
      const price = holding.security_detail
        ? holding.security_detail.unadjusted_closing_price.USD
        : holding.unadjusted_closing_price.USD;

      return holding.default_currency === 'USD' && !!price;
    });
    const usdAccount = selectedAccountList.find(
      (account) => account.currency === 'USD'
    );
    const excludeGicHoldings =
      selectedAccountOverview &&
      selectedAccountOverview.holdings.filter((holding) => !holding.gic_ticker);
    const gicHoldings =
      selectedAccountOverview &&
      selectedAccountOverview.holdings.filter((holding) => holding.gic_ticker);
    const hasNegativeWeights = hasNegativeWeight(
      selectedAccountOverview.holdings
    );
    const isNegativePort =
      getHoldTotalMarketValue(
        selectedAccountOverview,
        pwpaExchangeRate,
        user_region
      ) < 0;

    return (
      <div>
        {selectedAccountOverview !== null &&
          selectedAccountList.length !== 0 && (
            <Segment>
              <Header
                size="large"
                content={french ? 'Comptes groupés' : 'Grouped Accounts'}
                subheader={`${selectedAccountList.length} ${
                  french ? 'comptes sélectionnés' : 'accounts selected'
                }`}
              />
              <Divider fitted />
              <Grid>
                <Grid.Row columns={2}>
                  <Grid.Column width={7}>
                    <div style={{ marginTop: '1.3rem' }}>
                      <div style={{ float: 'left', padding: '0 2rem 1rem 0' }}>
                        <Header
                          size="medium"
                          content={
                            french ? 'Valeur du Compte' : 'Account Value:'
                          }
                          sub
                          style={{ margin: 0, fontWeight: 700 }}
                        />
                        <Value className="main">
                          {`$${this.parseNum(selectedAccountOverview.balance)}`}
                          <Flag
                            name={this.getFlag(
                              selectedAccountOverview.currency
                            )}
                            style={{
                              position: 'relative',
                              bottom: '7px',
                              marginLeft: 7,
                            }}
                          />
                        </Value>
                      </div>

                      <div style={{ float: 'left', padding: '0 0 1rem 0' }}>
                        <Header
                          size="medium"
                          content={
                            french ? 'Variation quotidienne' : 'Daily Change:'
                          }
                          sub
                          style={{ margin: 0 }}
                        />
                        <Value>{`${
                          selectedAccountOverview.change || 0
                        }%`}</Value>
                      </div>
                    </div>

                    <div style={{ clear: 'both' }}>
                      <Header
                        size="medium"
                        content={
                          french
                            ? 'Valeur totale des avoirs'
                            : 'Holdings Value:'
                        }
                        sub
                        style={{ margin: 0 }}
                      />
                      <Value>{`$${this.parseNum(
                        selectedAccountOverview.balance -
                          selectedAccountOverview.cash
                      )}`}</Value>
                    </div>
                    <div style={{ marginTop: '1.3rem' }}>
                      <Header
                        sub
                        size="medium"
                        style={{ margin: 0 }}
                        content={
                          <div>
                            {french ? 'Valeur en espèces' : 'Cash value:'}
                          </div>
                        }
                      />
                      <Value>{`$${this.parseNum(
                        selectedAccountOverview.cash
                      )}`}</Value>
                    </div>
                  </Grid.Column>
                  <Grid.Column width={9}>
                    {!selectedAccountOverview.holdings.length ||
                    !validHoldings.length ? null : (
                      <Segment basic compact>
                        <Header
                          size="medium"
                          textAlign="center"
                          style={{ margin: '0 0 1rem 0' }}
                        >
                          {french
                            ? 'Répartition nette de l’actif'
                            : 'Net Asset Allocation'}
                          <Header.Subheader>
                            {french
                              ? 'Comprend les positions sous-jacentes des fonds équilibrés et des fonds de répartition d’actifs.'
                              : 'Includes a breakdown of the underlying holdings of balanced and asset allocation funds.'}
                            <Popup
                              trigger={
                                <Icon
                                  name="question circle outline"
                                  style={{
                                    fontSize: '15px',
                                    verticalAlign: 'initial',
                                    color: 'dimgrey',
                                  }}
                                />
                              }
                              position="top center"
                              content={
                                french
                                  ? 'Analysez et utilisez le Scorecard pour approfondir les positions sous-jacentes afin de i) voir la répartition géographique par catégorie d’actif, et ii) vérifier la structure des fonds (p. ex., certains fonds utilisent des produits dérivés). Vous trouverez cette information dans Diversification → Catégorie d’actif (graphique du bas).'
                                  : 'Analyze and use the Scorecard to drill down to the underlying holdings to i) see the geographical split by asset class, and ii) check fund structure (e.g., some funds use derivatives). You will find this information in Diversification → Asset Class (bottom chart).'
                              }
                              wide
                            />
                          </Header.Subheader>
                        </Header>
                        <AssetChart
                          accounts={selectedAccountList}
                          overview={selectedAccountOverview}
                        />
                      </Segment>
                    )}
                  </Grid.Column>
                </Grid.Row>
              </Grid>
              {hasNegativeWeights ? (
                isNegativePort ? (
                  <Warning>
                    <Icon name="warning sign" style={{}} />
                    {translation[language].WARNING_NEGATIVEPORT}
                  </Warning>
                ) : (
                  <Warning>
                    <Icon name="warning sign" style={{}} />
                    {translation[language].WARNING_SHORTPOSITIONS}
                  </Warning>
                )
              ) : null}
              <CurrencyOption>
                <Button
                  className={cn('left', {
                    disabled: isNegativePort,
                  })}
                  icon="line chart"
                  color={colors.darkBlue}
                  onClick={() => {
                    if (
                      !hasCurrencyOptions(
                        user_region,
                        selectedAccountOverview.currency
                      )
                    ) {
                      this.analyze(
                        selectedAccountOverview.holdings,
                        user_region
                      );
                      dispatch(Storage.setItem('toggle_region', user_region));
                      dispatch(Portfolio.notSaveUrlSc());
                    }
                  }}
                  style={{ zIndex: 1 }}
                >
                  {french ? 'Analyser' : 'Analyze'}
                </Button>
                <div className="currency_item">
                  {!isNegativePort &&
                    hasCurrencyOptions(
                      user_region,
                      selectedAccountOverview.currency
                    ) && (
                      <span>
                        <StyledButton
                          className="basic"
                          color={colors.darkBlue}
                          onClick={() => {
                            this.analyze(
                              selectedAccountOverview.holdings,
                              'US'
                            );
                            dispatch(Storage.setItem('toggle_region', 'US'));
                            dispatch(Portfolio.notSaveUrlSc());
                          }}
                        >
                          <div className="flag">
                            <Flag name="us" style={{ height: 'inherit' }} />
                          </div>
                          <div style={{ marginLeft: '36px' }}>
                            {french ? 'En USD' : 'In USD'}
                          </div>
                        </StyledButton>
                        <StyledButton
                          className="basic"
                          color={colors.darkBlue}
                          onClick={() => {
                            this.analyze(
                              selectedAccountOverview.holdings,
                              'CA'
                            );
                            dispatch(Storage.setItem('toggle_region', 'CA'));
                            dispatch(Portfolio.notSaveUrlSc());
                          }}
                        >
                          <div className="flag">
                            <Flag name="ca" style={{ height: 'inherit' }} />
                          </div>
                          <div style={{ marginLeft: '36px' }}>
                            {french ? 'En CAD' : 'In CAD'}
                          </div>
                        </StyledButton>
                      </span>
                    )}
                  {selectedAccountOverview.cash > 0 && (
                    <StyledButton
                      className="basic"
                      color={colors.darkBlue}
                      onClick={() => history.push('/accounts/multiple/cash')}
                    >
                      <div className="flag cash">
                        <Icon
                          name="money"
                          style={{ fontSize: '1rem', margin: '0' }}
                        />
                      </div>
                      <div style={{ marginLeft: '36px' }}>
                        {french ? 'Argent liquide' : 'Incl. Cash'}
                      </div>
                    </StyledButton>
                  )}
                </div>
              </CurrencyOption>
              {/* <MoreActions>
                <div className="trigger">...</div>
                <div className="action-item">
                  <Button
                    className="left"
                    color={nbColors.red}
                    style={{
                      backgroundColor: 'white',
                      display: 'block',
                      width: '100%',
                      margin: '5px',
                      fontSize: french ? '11px' : '12px',
                    }}
                    icon="money"
                    onClick={() => history.push('/accounts/multiple/cash')}
                  >
                    {french ? 'Argent liquide' : 'Cash'}
                  </Button>
                </div>
              </MoreActions> */}
              <div style={{ clear: 'both' }} />

              {!notWealthica(partnerName) && (
                <p
                  style={{
                    fontWeight: 'bold',
                    position: 'absolute',
                    right: '7rem',
                    transform: 'translateY(2rem)',
                  }}
                >
                  {tablePage} {french ? 'sur' : 'of'} 2
                </p>
              )}

              <HoldingTable
                accountList={selectedAccountList}
                totalMarketValue={
                  selectedAccountOverview.balance - selectedAccountOverview.cash
                }
                holdings={excludeGicHoldings.slice(0, 10)}
                accountName=""
                title={
                  french ? 'Vos 10 plus grandes positions' : 'Top 10 Holdings'
                }
                isCombined
                increaseTablePage={this.increaseTablePage}
                decreaseTablePage={this.decreaseTablePage}
              />
              <GicTable
                user_region={user_region}
                gicHoldings={gicHoldings}
                totalMarketValue={
                  selectedAccountOverview.balance - selectedAccountOverview.cash
                }
                title="GIC"
                rate={pwpaExchangeRate}
              />
              {excludeGicHoldings.length !== 0 && (
                <P>
                  {user_region === 'CA' ? (
                    // eslint-disable-next-line react/jsx-fragments
                    <React.Fragment>
                      {selectedAccountOverview.currency === 'CAD'
                        ? french
                          ? 'Actuellement, nous disposons de données sur les actions/ETF canadiens et américains, les fonds communs de placement et les fonds séparés canadiens, ainsi que les fonds HISA en CAD/USD.'
                          : 'Currently, we have data for Canadian and US stocks/ETFs, Canadian mutual and segregated funds, and CAD/USD HISA funds.'
                        : french
                        ? 'Pour les comptes en dollars US, nous n’avons de données que pour les actions et FNB inscrits aux États-Unis, les fonds communs américains et les titres inscrits au Canada mais libellés en dollars US.'
                        : 'For USD accounts, we only have data for U.S.-listed stocks/ETFs, U.S. mutual funds, and Canadian-listed securities denominated in USD.'}
                      {user_region === 'CA' &&
                        selectedAccountOverview.currency === 'CAD' && (
                          <Popup
                            trigger={
                              <Icon
                                name="question circle outline"
                                style={{
                                  fontSize: '15px',
                                  verticalAlign: 'initial',
                                }}
                              />
                            }
                            content={
                              french
                                ? 'Les fonds de CÉIÉ en CAD/USD sont inclus dans l’analyse en utilisant le rendement historique d’un fonds de CÉIÉ en CAD/USD représentatif détenu par une banque.'
                                : 'CAD/USD HISA funds are included in the analysis using the historical rates of a representative bank-owned CAD/USD HISA fund.'
                            }
                            position="top center"
                          />
                        )}
                      <br />
                      {french ? (
                        'Toute autre valeur mobilière sera exclue de l’analyse.'
                      ) : (
                        // eslint-disable-next-line react/jsx-fragments
                        <React.Fragment>
                          Any other securities such as{' '}
                          <b>
                            private funds, individual bonds, and derivatives
                          </b>{' '}
                          will be excluded from the analysis.
                        </React.Fragment>
                      )}
                    </React.Fragment>
                  ) : (
                    'Currently we only have data for U.S. listed stocks and ETFs, U.S. mutual funds, Bitcoin, and Ethereum. Any other security will be excluded from the analysis.'
                  )}
                </P>
              )}
              {(!!usdHolding || !!usdAccount) &&
                excludeGicHoldings.length !== 0 && (
                  <P>
                    {french
                      ? 'Le taux de change USD/CAD utilisé est de '
                      : 'USD/CAD exchange rate used is '}
                    {pwpaExchangeRate || 1.3}
                  </P>
                )}
            </Segment>
          )}
      </div>
    );
  }
}

MultipleAccount.propTypes = {
  dispatch: PropTypes.func,
  history: PropTypes.object,
  selectedAccountList: PropTypes.array,
  selectedAccountOverview: PropTypes.object,
  pwpaExchangeRate: PropTypes.number,
  user_region: PropTypes.string,
  partnerName: PropTypes.string,
  french: PropTypes.bool,
  language: PropTypes.string,
};

MultipleAccount.defaultProps = {
  dispatch: () => {},
  history: {},
  selectedAccountOverview: {},
  selectedAccountList: [],
  pwpaExchangeRate: 0,
  user_region: 'CA',
  partnerName: '',
  french: false,
  language: 'en',
};

export default connect((state) => ({
  selectedAccountList: state.Accounts.selectedAccountList,
  selectedAccountOverview: state.Accounts.selectedAccountOverview,
  pwpaExchangeRate: state.Storage['pwpa-exchange-rate'],
  user_region: state.Storage.user_region,
  partnerName: state.Storage['partner-name'],
  french: state.Storage.language === 'fr',
  language: state.Storage.language,
}))(MultipleAccount);

const Value = styled.div`
  font-size: 1.8rem;
  padding: 7px 0;

  &.main {
    font-size: 2.4rem;
    font-weight: 700;
    color: ${nbColors.darkBlue};
  }
`;

const CurrencyOption = styled.div`
  position: absolute;
  top: 1.6rem;
  right: 5rem;
  z-index: 3;
  cursor: pointer;
  &:hover {
    & .currency_item {
      opacity: 1;
      visibility: visible;
      top: 18px;
      transition: opacity 200ms ease, top 150ms ease;
    }
  }
  .currency_item {
    position: absolute;
    top: 0px;
    right: 0;
    padding: 2rem 0 0 0;
    opacity: 0;
    visibility: hidden;
  }
`;

const StyledButton = styled.div`
  position: relative;
  border-radius: 4px;
  box-shadow: 0 0 0 1px rgba(34, 36, 38, 0.15) inset;
  font-size: 0.9rem;
  display: inline-block;
  min-height: 1em;
  min-width: 113px;
  height: 36px;
  border: none;
  text-align: center;
  outline: 0;
  transition: all 0.3s ease;
  overflow: hidden;
  color: rgba(0, 0, 0, 0.6);
  background-color: transparent;
  vertical-align: top;
  line-height: 36px !important;
  user-select: none !important;
  margin-top: 5px !important;
  background-color: white;
  &:hover {
    box-shadow: inset 0 0 0 1.5px ${colors.darkBlue} !important;
    color: rgba(0, 0, 0, 0.6);
  }
  .flag {
    height: 100%;
    position: absolute;
    color: white;
    top: 0;
    width: 36px;
    background-color: ${colors.darkBlue};
    padding-right: 36px;
    &:before {
      position: absolute;
      line-height: 1;
      font-size: 1em;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
    }
  }
  .cash {
    padding-right: 0px;
  }
`;

const MoreActions = styled.div`
  .action-item {
    position: absolute;
    top: 20px;
    right: 0;
    padding: 2rem 0.5rem 0.5rem 0.5rem;
    opacity: 0;
    visibility: hidden;
    transition: opacity 200ms ease, top 150ms ease;

    &:hover {
      opacity: 1;
      visibility: visible;
      top: 40px;
    }
  }

  .trigger {
    z-index: 2;
    position: absolute;
    top: 1.6rem;
    right: 2rem;
    font-size: 30px;
    background: white;
    color: rgba(0, 0, 0, 0.6);
    height: 35px;
    width: 35px;
    border: 1px solid rgba(0, 0, 0, 0.15);
    border-radius: 50%;
    line-height: 15px;
    text-align: center;
    cursor: pointer;
    transition: all 300ms ease;

    &:hover {
      border: 1px solid ${colors.darkBlue};
      color: ${colors.darkBlue};

      & ~ .action-item {
        opacity: 1;
        visibility: visible;
        top: 40px;
      }
    }
  }

  &:hover ~ .trigger {
    border: 1px solid ${colors.darkBlue};
    color: ${colors.darkBlue};
  }
`;

const P = styled.p`
  color: rgba(0, 0, 0, 0.5);
  font-style: italic;
  margin: 0.7rem auto;
  max-width: 100%;
  line-height: 1.1rem;
  text-align: center;
`;

const Warning = styled.p`
  color: ${colors.red};
  font-weight: bold;
  position: absolute;
  top: 4.5rem;
  right: 2rem;
`;
